'use client'

import { isEmpty } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'

import { asyncGlobalVariable } from '../../lib/utils'
import {
  ConsentPolicy,
  ConsentPolicyGdprCountry,
  consentPoliciesMatch,
  getConsentCategoryAliasForCode,
  isConsentCategoryCode,
} from '../_config/ConsentPolicy.config'

const validateIfOneTrustIsLoaded = () => {
  return typeof globalThis.OneTrust?.OnConsentChanged === 'function'
}

const OneTrustObservable$ = asyncGlobalVariable(() => {
  if (validateIfOneTrustIsLoaded()) {
    return globalThis.OneTrust
  } else {
    return undefined
  }
})

type OneTrustConsentChangeEvent = Parameters<
  Parameters<NonNullable<NonNullable<typeof OneTrust>['OnConsentChanged']>>[0]
>[0]

const getCurrentGdprCountry = (): ConsentPolicyGdprCountry | undefined => {
  if (typeof globalThis.OneTrust?.getGeolocationData !== 'function') {
    return undefined
  }
  return globalThis.OneTrust.getGeolocationData().country.toUpperCase()
}

const getDefaultConsentPolicy = (): ConsentPolicy => {
  return {
    essential: 'allowed',
    performance: 'blocked',
    functional: 'blocked',
    targeting: 'blocked',
    social: 'blocked',
  }
}

const didConsentWithdraw = (previous: ConsentPolicy, next: ConsentPolicy): boolean => {
  return (Object.keys(previous) as Array<keyof ConsentPolicy>).some(consentCategory => {
    return previous[consentCategory] === 'allowed' && next[consentCategory] === 'blocked'
  })
}

const getCurrentConsentPolicy = (): ConsentPolicy | undefined => {
  if (typeof globalThis.OnetrustActiveGroups !== 'string') {
    return
  }

  const activeGroupCodes = globalThis.OnetrustActiveGroups.split(',').filter(
    activeGroupCode => !isEmpty(activeGroupCode)
  )

  if (isEmpty(activeGroupCodes)) return

  const consentPolicy = getDefaultConsentPolicy()
  activeGroupCodes.forEach(activeGroupCode => {
    if (isConsentCategoryCode(activeGroupCode)) {
      const categoryAlias = getConsentCategoryAliasForCode(activeGroupCode)
      consentPolicy[categoryAlias] = 'allowed'
    }
  })
  return consentPolicy
}

const useConsentPolicyImpl = (
  initialState:
    | { consentPolicy: ConsentPolicy; gdprCountry: ConsentPolicyGdprCountry | undefined }
    | undefined
): {
  consentPolicy: ConsentPolicy
  gdprCountry: ConsentPolicyGdprCountry | undefined
  status: 'resolved' | 'pending'
} => {
  const [consentPolicyState, setConsentPolicyState] = useState<ConsentPolicy>(
    () => initialState?.consentPolicy ?? getCurrentConsentPolicy() ?? getDefaultConsentPolicy()
  )
  const [gdprCountryState, setGdprCountryState] = useState<ConsentPolicyGdprCountry | undefined>(
    initialState?.gdprCountry ?? getCurrentGdprCountry()
  )
  const [isOneTrustLoaded, setIsOneTrustLoaded] = useState(validateIfOneTrustIsLoaded())

  const onOneTrustConsentPolicyChange = useCallback(
    (event: OneTrustConsentChangeEvent) => {
      const newConsentPolicy: ConsentPolicy = getDefaultConsentPolicy()
      event.detail.forEach(activeGroupCode => {
        if (isConsentCategoryCode(activeGroupCode)) {
          const categoryAlias = getConsentCategoryAliasForCode(activeGroupCode)
          newConsentPolicy[categoryAlias] = 'allowed'
        }
      })
      if (!consentPoliciesMatch(newConsentPolicy, consentPolicyState)) {
        setConsentPolicyState(newConsentPolicy)
      }
    },
    [consentPolicyState]
  )

  const referenceToLatestOnChangeFunction = useRef(onOneTrustConsentPolicyChange)

  useEffect(() => {
    referenceToLatestOnChangeFunction.current = onOneTrustConsentPolicyChange

    const observableSubscription = OneTrustObservable$.subscribe(() => {
      // Skip if OneTrust hasn't finished loading yet — getCurrentConsentPolicy returns
      // undefined when the data is still partial, so we won't accidentally treat that as
      // "all blocked". If the user really did reduce their consent, we want that to land.
      const currentConsentPolicy = getCurrentConsentPolicy()
      if (currentConsentPolicy && !consentPoliciesMatch(currentConsentPolicy, consentPolicyState)) {
        setConsentPolicyState(currentConsentPolicy)
      }
      setIsOneTrustLoaded(true)

      // Subscribe to changes.
      if (typeof globalThis.OneTrust?.OnConsentChanged === 'function') {
        // Note: OneTrust.OnConsentChanged is not architected to handle calling it multiple times. To avoid bugs, we keep setting it to a function that references a pointer to the most recent `useCallback` result. So even if it has a stale onchange function, the stale function would still call the most recent callback.
        globalThis.OneTrust.OnConsentChanged(event => {
          referenceToLatestOnChangeFunction.current(event)
        })
      }
    })

    return () => {
      observableSubscription.unsubscribe()
    }
  }, [consentPolicyState, onOneTrustConsentPolicyChange])

  useEffect(() => {
    const observableSubscription = OneTrustObservable$.subscribe(() => {
      // Update based on what exists.
      const currentGdprCountry = getCurrentGdprCountry()
      if (currentGdprCountry !== gdprCountryState) {
        setGdprCountryState(currentGdprCountry)
      }
    })
    return () => {
      observableSubscription.unsubscribe()
    }
  }, [gdprCountryState])

  // GTM is not reactive to our consent policy. So if consent is withdrawn, we can help the user by refreshing for them without GTM around.
  const previousConsentPolicyRef = useRef(consentPolicyState)
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (previousConsentPolicyRef.current !== consentPolicyState) {
      if (didConsentWithdraw(previousConsentPolicyRef.current, consentPolicyState)) {
        window.location.reload()
      }
    }
    previousConsentPolicyRef.current = consentPolicyState
  }, [consentPolicyState])

  return {
    consentPolicy: consentPolicyState,
    gdprCountry: gdprCountryState,
    status: isOneTrustLoaded ? 'resolved' : 'pending',
  }
}

const ConsentPolicyContainer = createContainer(useConsentPolicyImpl)
ConsentPolicyContainer.Provider.displayName = 'ConsentPolicyProviderImpl'
export const ConsentPolicyProvider = ConsentPolicyContainer.Provider
export const useConsentPolicy = ConsentPolicyContainer.useContainer
